/**
 * Domain-based module visibility rules.
 *
 * Each group key maps to:
 *   "all"      → show every child in this group (no restriction)
 *   string[]   → whitelist; show only children whose keys match
 *   []         → hide the entire group
 *
 * Keys are normalized (no leading slash) to match both /pages and pages style
 * menu item keys.
 */

type GroupRule = "all" | string[]

export type DomainModuleConfig = Record<string, GroupRule>

/**
 * Fallback rules applied to any domain that has no entry in DOMAIN_MODULE_CONFIG.
 * Groups not listed here default to "all" (no restriction).
 */
export const DEFAULT_DOMAIN_CONFIG: DomainModuleConfig = {
  "group-system": ["settings", "tamda-one-settings", "websites"],
}

export const DOMAIN_MODULE_CONFIG: Record<string, DomainModuleConfig> = {
  "tamdamedia.eu": {
    "group-content": "all",
    "group-marketing": ["banners", "menus"],
    "group-business": [],
    "group-media": "all",
    "group-people": ["users", "roles"],
    "group-system": ["settings", "tamda-media-settings", "websites"],
  },
}

function resolveConfig(domain?: string): DomainModuleConfig {
  if (!domain) return DEFAULT_DOMAIN_CONFIG
  return DOMAIN_MODULE_CONFIG[domain] ?? DEFAULT_DOMAIN_CONFIG
}

/**
 * Complete list of route path segments (no leading slash) owned by each group.
 * Used to derive blocked paths for the middleware route guard.
 */
const GROUP_ALL_PATHS: Record<string, string[]> = {
  "group-content": ["pages", "posts", "category", "tags", "translations", "upload"],
  "group-marketing": ["banners", "campaigns", "campaign-subscribers", "menus"],
  "group-business": ["events", "subscriber", "tours", "stores", "suppliers", "letaky"],
  "group-media": ["video", "youtube-videos", "block-content"],
  "group-people": ["customer", "users", "roles", "careers", "career-form", "contact-forms"],
  "group-system": ["settings", "tamda-media-settings", "tamda-one-settings", "websites"],
}

function normalizeKey(key: string): string {
  return key.replace(/^\/+/, "")
}

/**
 * Returns the list of /admin sub-path segments that are blocked for the given
 * domain (e.g. ["campaigns", "events", "tamda-one-settings"]).
 * The middleware uses these to redirect blocked direct URL access.
 */
export function getBlockedAdminRoutes(domain?: string): string[] {
  const config = resolveConfig(domain)

  const blocked: string[] = []

  for (const [groupKey, rule] of Object.entries(config)) {
    const allPaths = GROUP_ALL_PATHS[groupKey] ?? []

    if (rule === "all") continue

    if (Array.isArray(rule) && rule.length === 0) {
      blocked.push(...allPaths)
      continue
    }

    if (Array.isArray(rule)) {
      const allowed = rule.map(normalizeKey)
      const blockedInGroup = allPaths.filter(p => !allowed.includes(normalizeKey(p)))
      blocked.push(...blockedInGroup)
    }
  }

  return blocked
}

/**
 * Returns true if the given /admin pathname is accessible for the domain.
 * pathname should be the full pathname, e.g. "/admin/campaigns/edit/1".
 */
export function isAdminPathAllowed(pathname: string, domain?: string): boolean {
  const blockedRoutes = getBlockedAdminRoutes(domain)
  if (blockedRoutes.length === 0) return true

  const subPath = pathname.replace(/^\/admin\/?/, "").split("/")[0]
  return !blockedRoutes.includes(subPath)
}
